import { Provider as NiceModalProvider, useModal } from '@ebay/nice-modal-react'
import { createRootRoute, Outlet, useSearch } from '@tanstack/react-router'
import { lazy, Suspense, useEffect, useRef } from 'react'
import { Provider as BusProvider } from 'react-bus'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'

import {
  ErrorPage,
  MessageModal,
  PasswordResetModal,
} from '../components/common'
import AgeVerificationModal from '../components/common/age-verification-modal'
import DownloadAppModal from '../components/common/download-app-modal'
import BottomNav from '../components/layouts/bottom-nav'
import { SIDEBAR_MODAL_ID } from '../components/sidebar/modal-id'
import SportsLobbyHost from '../components/sports/sports-lobby-host'
import useAdsInfo from '../hooks/useAdsInfo'
import useAnnouncements from '../hooks/useAnnouncements'
import useComponents from '../hooks/useComponents'
import useContinue from '../hooks/useContinue'
// import useDepositEvents from '../hooks/useDepositEvents'
import useDimensions from '../hooks/useDimensions'
import useFbPixel from '../hooks/useFbPixel'
import useIframePrevention from '../hooks/useIframePrevention'
import usePaymentChannels from '../hooks/usePaymentChannels'
import useScrollToTop from '../hooks/useScrollToTop'
import useSettings from '../hooks/useSettings'
import useSidebarPreload from '../hooks/useSidebarPreload'
import useSportsMemberToken from '../hooks/useSportsMemberToken'
import useSportsPrefetch from '../hooks/useSportsPrefetch'
import useTheme from '../hooks/useTheme'
import useWhatsApp from '../hooks/useWhatsApp'
import { useBoundStore } from '../store/store'
import type { ApiResponse } from '../types/api'
import type { ResetCredentials } from '../types/user'
import { api } from '../utils/api-client'
import isChromelessRoute from '../utils/isChromelessRoute'
import { isInternalRedirect } from '../utils/redirect'

// The mobile side menu, mounted declaratively (below) rather than on demand
// through NiceModal's placeholder. A placeholder-mounted modal opens in two
// commits — nice-modal-react mounts it with `visible: false` and only flips the
// flag in a follow-up effect (see its `delayVisible`) — so the first tap
// rendered the whole menu twice before the slide could start. Mounted up front
// it is already there, and `show()` becomes a single class flip. `keepMounted`
// is what creates the modal state entry the wrapper needs to render at all, so
// the drawer must never be closed with `remove()`, only `hide()`, or it
// unmounts for good.
//
// Mounted here rather than in Header because the root route never unmounts, so
// this mounts once for the session and there is exactly one drawer regardless of
// how many headers a page renders.
//
// Lazy purely for chunking: root.tsx *is* the entry chunk, and a static import
// would move the whole drawer into it. It is still rendered unconditionally on
// mobile — the import kicks off on first render, off the critical path, and the
// drawer costs the same bytes it always did, just in its own chunk instead of
// the header's. A tap landing before that chunk resolves is safe: show() records
// the modal state, and the delayVisible effect opens the drawer as soon as the
// wrapper mounts.
const Sidebar = lazy(() => import('../components/sidebar'))

const ResetPasswordHandler = () => {
  const messageModal = useModal(MessageModal)
  const passwordResetModal = useModal(PasswordResetModal)
  const { t } = useTranslation('common')
  const { reset_password } = useSearch({ from: rootRoute.id })
  const processedHashes = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!reset_password) return
    if (processedHashes.current.has(reset_password)) return
    processedHashes.current.add(reset_password)

    const currentUsername = useBoundStore.getState().user.username
    if (currentUsername) {
      messageModal.show({
        title: '',
        message: t('already-logged-in'),
      })
      return
    }
    const reqBody = { type: 'verify_reset_password', hash: reset_password }
    const verify = async () => {
      try {
        const res = await api.post('/request.php', reqBody)
        const { status, info } = res.data as ApiResponse<
          ResetCredentials | string
        >
        if (status === 1 && typeof info !== 'string') {
          passwordResetModal.show({
            code: info?.code ?? '',
            email: info?.email ?? '',
          })
        } else if (typeof info === 'string') {
          messageModal.show({
            title: t('system'),
            message: info,
          })
        }
      } catch {
        messageModal.show({
          title: t('system'),
          message: 'Error inesperado, intenta de nuevo',
        })
      }
    }
    verify()
  }, [reset_password])

  return null
}

export const rootRoute = createRootRoute({
  validateSearch: (
    search: Record<string, unknown>,
  ): { reset_password?: string; redirectUrl?: string } => ({
    reset_password:
      typeof search.reset_password === 'string'
        ? search.reset_password
        : undefined,
    redirectUrl: isInternalRedirect(search.redirectUrl)
      ? search.redirectUrl
      : undefined,
  }),
  component: () => {
    useComponents()
    const { isDesktop } = useDimensions()
    useTheme()
    useContinue()
    useAnnouncements()
    useScrollToTop()
    useSettings()
    // Deposit-event reporting (GTM purchase / Meta Purchase / Twitter)
    // temporarily disabled — uncomment this and the import above to restore.
    // useDepositEvents()
    usePaymentChannels()
    useAdsInfo()
    useFbPixel()
    useIframePrevention()
    useWhatsApp()
    useSidebarPreload()
    useSportsPrefetch()
    useSportsMemberToken()

    return (
      <BusProvider>
        <NiceModalProvider>
          <ResetPasswordHandler />
          <Helmet>
            <link
              rel="canonical"
              href={window.location.href.split('?')[0].replace(/\/+$/, '')}
            />
          </Helmet>
          {/* See the note above `Sidebar`. */}
          {!isDesktop && (
            <Suspense fallback={null}>
              <Sidebar id={SIDEBAR_MODAL_ID} keepMounted />
            </Suspense>
          )}
          <Outlet />
          {/* Outlives the /sports route on purpose — see the component. */}
          <SportsLobbyHost />
          <DownloadAppModal />
          <AgeVerificationModal />
          {!window.ReactNativeWebView &&
            !isDesktop &&
            !isChromelessRoute(location.pathname) && <BottomNav />}
        </NiceModalProvider>
      </BusProvider>
    )
  },
  errorComponent: ErrorPage,
})
